import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, Empty, Select } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Eye, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = [
  { value: "main_bin", label: "Main Consignment Bin" },
  { value: "regional", label: "Regional Warehouse" },
  { value: "fitment_centre", label: "Fitment Centre" },
  { value: "technician", label: "In-house Technician" },
];

const PROVINCES = ["EC", "FS", "GAU", "KZN", "LIM", "MPU", "NC", "NW", "WC"];
const emptyForm = {
  name: "", type: "", region: "", parent_id: "",
  account_number: "", fc_reg: "", grade: "A", province: "", mini_zone: "", area: "",
  operating_status: "Active", emails: "", telephones: "", contact_name: "", physical_address: "",
  weekend_hours: "", account_handler: "", mobile: true, dotsure: true, telesure: true,
};

const AccrBadge = ({ label }) => (
  <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wide inline-flex items-center gap-0.5" style={{ background: "#f0fdfa", color: "#00857C" }}>
    <ShieldCheck className="h-2.5 w-2.5" />{label}
  </span>
);

export default function Locations() {
  const { user } = useAuth();
  const [locs, setLocs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [view, setView] = useState(null);
  const canManage = ["operations_controller", "warehouse_manager", "system_admin"].includes(user.role);

  const load = useCallback(() => { api.get("/locations").then((r) => setLocs(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const regionals = locs.filter((l) => l.type === "regional");
  const isFC = form.type === "fitment_centre" || form.type === "technician";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.name || !form.type) { toast.error("Name and type required"); return; }
    const payload = {
      name: form.name, type: form.type, region: form.region || null, parent_id: form.parent_id || null,
    };
    if (isFC) {
      Object.assign(payload, {
        account_number: form.account_number || null, fc_reg: form.fc_reg || null, grade: form.grade || null,
        province: form.province || null, mini_zone: form.mini_zone || null, area: form.area || null,
        operating_status: form.operating_status || null,
        emails: form.emails ? form.emails.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : null,
        telephones: form.telephones || null, contact_name: form.contact_name || null,
        physical_address: form.physical_address || null, weekend_hours: form.weekend_hours || null,
        account_handler: form.account_handler || null,
        accreditations: { mobile: !!form.mobile, dotsure: !!form.dotsure, telesure: !!form.telesure },
      });
    }
    try {
      await api.post("/locations", payload);
      toast.success("Location created");
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/locations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const label = (t) => TYPES.find((x) => x.value === t)?.label || t;

  return (
    <div>
      <PageHeader title="Locations & Fitment Centres" subtitle="Main bin, regional warehouses, and accredited fitment centres." />
      {canManage && (
        <Panel title="Add Location" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div><Label className="overline text-slate-500">Name</Label><Input data-testid="loc-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm mt-1" /></div>
            <div><Label className="overline text-slate-500">Type</Label><div className="mt-1"><Select testid="loc-type" value={form.type} onChange={(v) => set("type", v)} options={TYPES} placeholder="Type" /></div></div>
            <div><Label className="overline text-slate-500">Region</Label><Input data-testid="loc-region" value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="e.g. KwaZulu-Natal" className="rounded-sm mt-1" /></div>
            <div><Label className="overline text-slate-500">Regional parent (for sites)</Label><div className="mt-1"><Select testid="loc-parent" value={form.parent_id} onChange={(v) => set("parent_id", v)} options={regionals.map((r) => ({ value: r.id, label: r.name }))} placeholder="—" /></div></div>
            {!isFC && <Button data-testid="loc-create" onClick={create} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Add</Button>}
          </div>

          {isFC && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="overline text-[#00857C] mb-3">Fitment Centre Profile</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div><Label className="overline text-slate-500">Account no.</Label><Input data-testid="fc-account" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">FC Reg no.</Label><Input data-testid="fc-reg" value={form.fc_reg} onChange={(e) => set("fc_reg", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">Grade</Label><Input data-testid="fc-grade" value={form.grade} onChange={(e) => set("grade", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">Province</Label><div className="mt-1"><Select testid="fc-province" value={form.province} onChange={(v) => set("province", v)} options={PROVINCES.map((p) => ({ value: p, label: p }))} placeholder="—" /></div></div>
                <div><Label className="overline text-slate-500">Mini Zone</Label><Input data-testid="fc-zone" value={form.mini_zone} onChange={(e) => set("mini_zone", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">Area</Label><Input data-testid="fc-area" value={form.area} onChange={(e) => set("area", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">Contact name</Label><Input data-testid="fc-contact" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">Telephone(s)</Label><Input data-testid="fc-tel" value={form.telephones} onChange={(e) => set("telephones", e.target.value)} className="rounded-sm mt-1" /></div>
                <div className="md:col-span-2"><Label className="overline text-slate-500">Email(s) — comma/newline</Label><Input data-testid="fc-emails" value={form.emails} onChange={(e) => set("emails", e.target.value)} placeholder="primary@fc.co.za, admin@fc.co.za" className="rounded-sm mt-1" /></div>
                <div className="md:col-span-2"><Label className="overline text-slate-500">Physical address</Label><Input data-testid="fc-address" value={form.physical_address} onChange={(e) => set("physical_address", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">Weekend hours</Label><Input data-testid="fc-weekend" value={form.weekend_hours} onChange={(e) => set("weekend_hours", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="overline text-slate-500">Account handler</Label><Input data-testid="fc-handler" value={form.account_handler} onChange={(e) => set("account_handler", e.target.value)} className="rounded-sm mt-1" /></div>
                <div className="md:col-span-2 flex items-end gap-4 pb-1">
                  <label className="flex items-center gap-1.5 text-sm text-slate-600"><input data-testid="fc-mobile" type="checkbox" checked={form.mobile} onChange={(e) => set("mobile", e.target.checked)} /> Mobile</label>
                  <label className="flex items-center gap-1.5 text-sm text-slate-600"><input data-testid="fc-dotsure" type="checkbox" checked={form.dotsure} onChange={(e) => set("dotsure", e.target.checked)} /> Dotsure</label>
                  <label className="flex items-center gap-1.5 text-sm text-slate-600"><input data-testid="fc-telesure" type="checkbox" checked={form.telesure} onChange={(e) => set("telesure", e.target.checked)} /> Telesure</label>
                </div>
              </div>
              <div className="mt-3"><Button data-testid="loc-create" onClick={create} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Add Fitment Centre</Button></div>
            </div>
          )}
        </Panel>
      )}

      <Panel>
        {locs.length === 0 ? <Empty>No locations yet. Add a Main Consignment Bin first.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="locations-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Region</th>
                <th className="py-2 pr-3">Account</th><th className="py-2 pr-3">Grade</th><th className="py-2 pr-3">Contact</th>
                <th className="py-2 pr-3">Accreditations</th><th></th></tr></thead>
              <tbody>
                {locs.map((l) => {
                  const acc = l.accreditations || {};
                  return (
                    <tr key={l.id} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-medium text-[#0B2545]">{l.name}</td>
                      <td className="py-2 pr-3 text-slate-600">{label(l.type)}</td>
                      <td className="py-2 pr-3 text-slate-500">{l.region || "—"}</td>
                      <td className="py-2 pr-3 text-slate-500 font-label text-xs">{l.account_number || "—"}</td>
                      <td className="py-2 pr-3 text-slate-500">{l.grade || "—"}</td>
                      <td className="py-2 pr-3 text-slate-500 text-xs">{l.contact_name || "—"}</td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {acc.mobile && <AccrBadge label="Mobile" />}
                          {acc.dotsure && <AccrBadge label="Dotsure" />}
                          {acc.telesure && <AccrBadge label="Telesure" />}
                        </div>
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {l.type === "fitment_centre" && (
                          <button data-testid={`loc-view-${l.id}`} onClick={() => setView(l)} className="text-slate-400 hover:text-[#00857C] mr-3"><Eye className="h-4 w-4 inline" /></button>
                        )}
                        {canManage && <button data-testid={`loc-del-${l.id}`} onClick={() => del(l.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4 inline" /></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Dialog open={!!view} onOpenChange={(o) => { if (!o) setView(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-[#0B2545]">{view?.name}</DialogTitle></DialogHeader>
          {view && (
            <div className="text-sm space-y-2" data-testid="fc-detail">
              {[
                ["Account no.", view.account_number], ["FC Reg no.", view.fc_reg], ["Grade", view.grade],
                ["Province", view.province], ["Region", view.region], ["Mini Zone", view.mini_zone],
                ["Area", view.area], ["Operating status", view.operating_status],
                ["Contact name", view.contact_name], ["Telephone(s)", view.telephones],
                ["Email(s)", (view.emails || []).join(", ")], ["Physical address", view.physical_address],
                ["Weekend hours", view.weekend_hours], ["Account handler", view.account_handler],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1.5">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-[#0B2545] font-medium text-right">{v || "—"}</span>
                </div>
              ))}
              <div className="flex justify-between gap-4 py-1.5">
                <span className="text-slate-500">Accreditations</span>
                <span className="flex gap-1">
                  {(view.accreditations || {}).mobile && <AccrBadge label="Mobile" />}
                  {(view.accreditations || {}).dotsure && <AccrBadge label="Dotsure" />}
                  {(view.accreditations || {}).telesure && <AccrBadge label="Telesure" />}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
